import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Rating,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { getRestaurants, deleteRestaurant } from '../services/restaurantService';

function Restaurants() {
  const [restaurants, setRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('name');

  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    fetchRestaurants();
  }, []);

  // Filter and sort restaurants when search query, sort order, or restaurants change
  useEffect(() => {
    let filtered = [...restaurants];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(restaurant => {
        switch (searchField) {
          case 'name':
            return restaurant.name?.toLowerCase().includes(query);
          case 'street':
            return restaurant.street?.toLowerCase().includes(query);
          case 'city':
            return restaurant.city?.toLowerCase().includes(query);
          case 'state':
            return restaurant.state?.toLowerCase().includes(query);
          case 'bbqStyle':
            return restaurant.bbqStyle?.toLowerCase().includes(query);
          case 'all':
            return (
              restaurant.name?.toLowerCase().includes(query) ||
              restaurant.street?.toLowerCase().includes(query) ||
              restaurant.city?.toLowerCase().includes(query) ||
              restaurant.state?.toLowerCase().includes(query) ||
              restaurant.bbqStyle?.toLowerCase().includes(query) ||
              restaurant.description?.toLowerCase().includes(query)
            );
          default:
            return true;
        }
      });
    }
    
    // Apply sorting
    filtered = sortRestaurants(filtered, orderBy, order);
    
    setFilteredRestaurants(filtered);
  }, [searchQuery, searchField, restaurants, order, orderBy]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const data = await getRestaurants();
      setRestaurants(data);
      setFilteredRestaurants(sortRestaurants(data, orderBy, order));
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load restaurants',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const sortRestaurants = (restaurantList, property, sortOrder) => {
    const sortedList = [...restaurantList].sort((a, b) => {
      let valueA, valueB;
      
      // Handle special properties
      if (property === 'rating') {
        valueA = a[property] || 0;
        valueB = b[property] || 0;
      } else if (property === 'status') {
        valueA = a.openForBusiness ? 1 : 0;
        valueB = b.openForBusiness ? 1 : 0;
      } else {
        valueA = (a[property] || '').toString().toLowerCase();
        valueB = (b[property] || '').toString().toLowerCase();
      }
      
      // Compare values
      if (valueA < valueB) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sortedList;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchFieldChange = (event) => {
    setSearchField(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleDeleteClick = (restaurant) => {
    setRestaurantToDelete(restaurant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!restaurantToDelete) return;
    
    try {
      await deleteRestaurant(restaurantToDelete.id);
      setRestaurants(prevRestaurants => 
        prevRestaurants.filter(r => r.id !== restaurantToDelete.id)
      );
      setSnackbar({
        open: true,
        message: `${restaurantToDelete.name} deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete restaurant',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Table header cell with sort functionality
  const SortableTableCell = ({ id, label, align = 'left' }) => (
    <TableCell 
      sortDirection={orderBy === id ? order : false}
      align={align}
    >
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={() => handleRequestSort(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Restaurants
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/restaurants/add"
          >
            Add Restaurant
          </Button>
        </Box>
        
        {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search restaurants..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} edge="end" size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Search In</InputLabel>
              <Select
                value={searchField}
                label="Search In"
                onChange={handleSearchFieldChange}
              >
                <MenuItem value="all">All Fields</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="street">Street</MenuItem>
                <MenuItem value="city">City</MenuItem>
                <MenuItem value="state">State</MenuItem>
                <MenuItem value="bbqStyle">BBQ Style</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredRestaurants.length} {filteredRestaurants.length === 1 ? 'restaurant' : 'restaurants'} found
            </Typography>
            {searchQuery && (
              <Button size="small" onClick={clearSearch}>
                Clear Search
              </Button>
            )}
          </Box>
        </Paper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell id="name" label="Restaurant" />
                  <SortableTableCell id="street" label="Street" />
                  <SortableTableCell id="city" label="City" />
                  <SortableTableCell id="state" label="State" />
                  <SortableTableCell id="bbqStyle" label="BBQ Style" />
                  <SortableTableCell id="rating" label="Rating" />
                  <SortableTableCell id="status" label="Status" />
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRestaurants.map((restaurant) => (
                  <TableRow key={restaurant.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {restaurant.logoPhotoURL ? (
                          <Avatar 
                            src={restaurant.logoPhotoURL} 
                            alt={restaurant.name}
                            sx={{ mr: 2, width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {restaurant.name?.charAt(0).toUpperCase() || '?'}
                          </Avatar>
                        )}
                        <Typography variant="subtitle2">
                          {restaurant.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{restaurant.street || '-'}</TableCell>
                    <TableCell>{restaurant.city || '-'}</TableCell>
                    <TableCell>{restaurant.state || '-'}</TableCell>
                    <TableCell>{restaurant.bbqStyle || '-'}</TableCell>
                    <TableCell>
                      <Rating 
                        value={restaurant.rating || 0} 
                        precision={0.5} 
                        readOnly 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {restaurant.openForBusiness ? (
                        <Chip 
                          label="Open" 
                          color="success" 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="Closed" 
                          color="error" 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        component={RouterLink} 
                        to={`/restaurants/edit/${restaurant.id}`}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteClick(restaurant)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRestaurants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" sx={{ py: 2 }}>
                        {searchQuery 
                          ? 'No restaurants match your search criteria.' 
                          : 'No restaurants found. Add your first restaurant!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {restaurantToDelete?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Restaurants;